import { ChevronDown, X } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export interface MultiselectOption<T extends string> {
  value: T;
  label: string;
}

export interface MultiselectFilterProps<T extends string> {
  /** Label rendered when no chips are selected (e.g. "Register Type"). */
  label: string;
  options: ReadonlyArray<MultiselectOption<T>>;
  selected: ReadonlyArray<T>;
  onChange: (next: T[]) => void;
  /** Optional className applied to the trigger row. */
  className?: string;
}

/**
 * Minimal multiselect filter: a trigger button that summarizes the current
 * selection (label + chips), opening a popover with checkbox options. When a
 * selection is active, a sibling clear button (X) is rendered next to the
 * trigger — kept as a sibling rather than nested to avoid invalid
 * button-inside-button HTML.
 *
 * Empty selection is "unconstrained" (no chips, just the label). Mirrors the
 * styling of the single shadcn `Select` component used elsewhere on this page.
 */
export function MultiselectFilter<T extends string>({
  label,
  options,
  selected,
  onChange,
  className,
}: MultiselectFilterProps<T>) {
  const selectedSet = new Set(selected);

  function toggle(value: T) {
    if (selectedSet.has(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  const hasSelection = selected.length > 0;
  const summary = hasSelection
    ? options
      .filter((o) => selectedSet.has(o.value))
      .map((o) => o.label)
      .join(', ')
    : label;

  return (
    <div className='flex items-center gap-1'>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            className={`min-w-[200px] justify-between font-normal ${className ?? ''}`}
            aria-label={label}
          >
            <span className='truncate'>
              {hasSelection ? `${label}: ${summary}` : label}
            </span>
            <ChevronDown className='ml-2 h-4 w-4 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-56 p-2' align='start'>
          <div className='flex flex-col gap-1'>
            {options.map((option) => {
              const checked = selectedSet.has(option.value);
              return (
                <label
                  key={option.value}
                  className='flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted'
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      {hasSelection && (
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          aria-label={`Clear ${label}`}
          onClick={() => onChange([])}
        >
          <X className='h-4 w-4' />
        </Button>
      )}
    </div>
  );
}
